import { ClarionButton } from '@clarion/ClarionButton';
import { ClarionDialog, ClarionDialogFooter } from '@clarion/ClarionDialog';
import { Icon } from '@clarion/ClarionIcon';
import { ClarionSwitch as Switch } from '@clarion/ClarionSwitch';
import { ClarionTag as Tag } from '@clarion/ClarionTag';
import { ClarionTextField } from '@clarion/ClarionTextField';
import { useMemo, useState } from 'react';
import { useSideNavigation } from '../components/navigation/SideNavigationContext';
import { FlagName, getFlagOverrides, getFlags, resetFlagOverrides, setFlagOverride } from './flags';

/**
 * Provides UI to view and override flags from the user's localStorage.
 * The button is visible when flags are overridden, in development mode, or with ?flagOverrides query param is present.
 */
export default function FlagOverrides() {
  const [isOpen, setIsOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const flags = getFlags();
  const flagOverrides = getFlagOverrides();
  const overriddenFlagCount = Object.keys(flagOverrides).length;
  const { isCollapsed } = useSideNavigation();
  const showOverrides =
    overriddenFlagCount > 0 ||
    process.env.NODE_ENV === 'development' ||
    window.location.search.includes('flagOverrides');

  const filteredFlags = useMemo(() => {
    const query = filterQuery.trim().toLowerCase();
    return Object.entries(flags).filter(([flag]) => !query || flag.toLowerCase().includes(query));
  }, [flags, filterQuery]);

  const handleClose = () => {
    setIsOpen(false);
    setFilterQuery('');
  };

  if (!showOverrides) return null;

  return (
    <>
      <ClarionButton
        variant="minimal"
        size="large"
        icon={<Icon icon="flag" intent={overriddenFlagCount ? 'warning' : 'none'} />}
        tooltip="Flag overrides"
        tooltipPlacement="right"
        textAlign="left"
        fill
        onClick={() => setIsOpen(true)}
      >
        {!isCollapsed && (
          <div className="flex justify-between items-center gap-2">
            Flags
            {overriddenFlagCount > 0 && (
              <Tag minimal intent="warning">
                {overriddenFlagCount}
              </Tag>
            )}
          </div>
        )}
      </ClarionButton>

      <ClarionDialog
        isOpen={isOpen}
        onClose={handleClose}
        title={`Flag overrides (${overriddenFlagCount})`}
        maxWidth={480}
        className="h-full flex flex-col min-h-0"
        style={{ height: '100%', maxHeight: '100%' }}
      >
        <ClarionTextField
          aria-label="Filter flags"
          autoFocus
          autoComplete="off"
          icon="search"
          placeholder="Filter flags..."
          value={filterQuery}
          onChange={setFilterQuery}
          fill
        />

        <div className="flex-1 min-h-0 overflow-auto mt-4">
          <div className="space-y-2">
            {filteredFlags.length > 0 ? (
              filteredFlags.map(([flag, enabled]) => (
                <Switch
                  key={flag}
                  children={`${flag} ${typeof flagOverrides[flag as FlagName] === 'boolean' ? ' *' : ''}`}
                  defaultSelected={enabled}
                  onChange={() => setFlagOverride(flag as FlagName, !enabled)}
                />
              ))
            ) : (
              <p className="opacity-80">No flags match your filter.</p>
            )}
          </div>
        </div>

        <ClarionDialogFooter className="shrink-0">
          <p className="opacity-80 flex-1">* overridden</p>
          <ClarionButton variant="outlined" onClick={resetFlagOverrides}>
            Reset overrides
          </ClarionButton>
          <ClarionButton intent="primary" onClick={() => window.location.reload()}>
            Save
          </ClarionButton>
        </ClarionDialogFooter>
      </ClarionDialog>
    </>
  );
}
